import DOMPurify from "dompurify";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  BUSY_CALL_STATUSES,
  useAskCall,
  useCall,
  useEmailCall,
  useRenameCallParticipant,
  useSummariseCall,
} from "../../hooks/useCalls";
import { useMeetingNotesTemplates } from "../../hooks/useMeetingNotesTemplates";
import { colors, fonts } from "../../style";
import {
  formatDayHeading,
  formatTime,
  meetingAsText,
  PROGRESS_COPY,
} from "../../utils/meetingFormat";
import { CallEmailPanel } from "./CallEmailPanel";
import { AskTab } from "../meetings/MeetingAsk";
import { SummaryTab } from "../meetings/MeetingSummary";
import { TranscriptTab } from "../meetings/MeetingTranscript";
import {
  DETAIL_TABS,
  DetailHeader,
  detailTitle,
} from "../meetings/MeetingView";
import { SummariseTemplatePicker } from "../sidekick/SummariseTemplatePicker";
import {
  BackLink,
  Banner,
  Btn,
  Chip,
  DetailCard,
  ErrorState,
  Spinner,
  Tabs,
  ui,
} from "../ui";
import {
  callAsMeeting,
  callLength,
  callZoneLabel,
  directionFor,
} from "./callFormat";

const PROMPTS = [
  "Write a follow up email to the customer",
  "List a few follow up steps",
  "Write a short rhyme about this call",
];

// AI output that can be steered by what was said on the call, so only plain
// text formatting gets through: no links, images, forms or styles
const ANSWER_TAGS = [
  "p",
  "br",
  "ul",
  "ol",
  "li",
  "strong",
  "em",
  "b",
  "i",
  "h3",
  "h4",
];
const cleanAnswer = (html) =>
  DOMPurify.sanitize(html ?? "", {
    ALLOWED_TAGS: ANSWER_TAGS,
    ALLOWED_ATTR: [],
  });
const answerText = (html) => {
  const el = document.createElement("div");
  el.innerHTML = cleanAnswer(html);
  return el.textContent.trim();
};

// Memoised so typing the next question doesn't re-sanitise every answer
function Answer({ html }) {
  const clean = useMemo(() => cleanAnswer(html), [html]);
  return (
    <div
      className="sk-answer"
      style={{ fontSize: "14px", lineHeight: 1.6 }}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}

// An iPECS call laid out like a meeting's full page. onBack returns
// to the list it was opened from.
export function CallView({ sqid, onBack }) {
  const { data: call, error, isLoading, refetch } = useCall(sqid);
  const summarise = useSummariseCall(sqid);
  const renameSpeaker = useRenameCallParticipant(sqid);
  const ask = useAskCall(sqid);
  const email = useEmailCall(sqid);
  const { templates, isLoading: templatesLoading } = useMeetingNotesTemplates();
  const [pickedTab, setTab] = useState(null);
  const [picking, setPicking] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const back = <BackLink onClick={onBack}>Sidekick</BackLink>;

  const shell = (children) => (
    <div
      style={{
        fontFamily: fonts.system,
        color: colors.textStrong,
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        padding: "24px 32px 40px",
        maxWidth: "1200px",
        boxSizing: "border-box",
      }}
    >
      {back}
      {children}
    </div>
  );

  if (!call) {
    return shell(
      <div
        style={{
          padding: "28px 0",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          alignItems: "center",
        }}
      >
        {isLoading ? (
          <Spinner size={22} />
        ) : (
          <ErrorState
            card={false}
            onRetry={error?.response?.status !== 404 && (() => refetch())}
            retryLabel="Try again"
          >
            {error?.response?.status === 404
              ? "This call doesn't exist or you can't see it."
              : "Couldn't load this call."}
          </ErrorState>
        )}
      </div>,
    );
  }

  const meeting = callAsMeeting(call);
  const status = call.status?.value;
  const busy = BUSY_CALL_STATUSES.includes(status);
  const failed = status === "FAILED" || status === "DID_NOT_TRANSCRIBE";
  const hasSummary =
    status !== "SUMMARISING" &&
    (!!meeting.summary || meeting.meeting_notes.length > 0);
  const canSummarise = call.can_summarise && !hasSummary && !busy;
  const tab =
    pickedTab ??
    (hasSummary || status === "SUMMARISING" ? "summary" : "transcript");
  const kind = directionFor(call.direction);
  const zone = callZoneLabel(call.effective_timezone);
  const length = callLength(call.duration);

  const runSummarise = (templateId) => {
    setPicking(false);
    summarise.mutate(templateId, {
      onSuccess: () => setTab("summary"),
      // The API's message says when the Sidekick minutes have run out
      onError: (e) =>
        toast(
          e.response?.data?.message ?? "There was an error with your request",
          { type: "error", autoClose: 10000, toastId: "sidekick-limit-error" },
        ),
    });
  };

  // With no custom templates there's nothing to pick
  const startSummarise = () =>
    !templatesLoading && templates.length === 0
      ? runSummarise(undefined)
      : setPicking(true);

  // navigator.clipboard is missing outside secure contexts
  const copy = () =>
    (
      navigator.clipboard?.writeText(meetingAsText(meeting, when)) ??
      Promise.reject()
    ).then(
      () => toast("Notes copied", { type: "success" }),
      () => toast("Couldn't copy the notes", { type: "error" }),
    );

  const when = call.start_utc
    ? `${formatDayHeading(call.start_utc, call.effective_timezone)}, ${formatTime(call.start_utc, call.effective_timezone)}`
    : call.start;

  const meta = [
    call.user_name && call.tel_number,
    call.user_ext && `Ext ${call.user_ext}`,
    when,
    length,
    zone,
    call.meeting_notes_template && hasSummary
      ? `${call.meeting_notes_template} template`
      : null,
  ].filter(Boolean);

  const header = (
    <DetailHeader
      chip={
        <Chip color={kind.color} icon={kind.icon}>
          {kind.label}
        </Chip>
      }
      title={
        <h1 style={{ ...detailTitle(26), overflowWrap: "anywhere" }}>
          {meeting.title}
        </h1>
      }
      meta={meta.join(" · ")}
      actions={
        <>
          {hasSummary && (
            <Btn icon="copy" onClick={copy}>
              Copy notes
            </Btn>
          )}
          {call.has_transcription && (
            <Btn icon="mail" onClick={() => setEmailing(true)}>
              {hasSummary ? "Email notes" : "Email transcript"}
            </Btn>
          )}
        </>
      }
    />
  );

  const states = (
    <>
      {busy && (
        <Banner tone="warning" icon="refresh" title={PROGRESS_COPY[status]}>
          Usually a minute or two. This page updates by itself.
          {status === "SUMMARISING" && call.has_transcription && (
            <span style={ui.muted}>
              {" "}
              The transcript is ready to read while you wait.
            </span>
          )}
        </Banner>
      )}
      {failed && (
        <Banner
          tone="error"
          icon="alert"
          title={
            status === "DID_NOT_TRANSCRIBE"
              ? "Not transcribed"
              : "Couldn't process this call"
          }
        >
          {meeting.transcriptPlaceholder ||
            "Sidekick couldn't transcribe this recording, so there's nothing to summarise."}
        </Banner>
      )}
      {canSummarise && (
        <Banner
          tone="info"
          icon="file"
          title="Not summarised yet"
          action={
            <div>
              <Btn
                variant="primary"
                loading={summarise.isLoading}
                onClick={startSummarise}
              >
                Summarise
              </Btn>
            </div>
          }
        >
          Sidekick can write a summary, meeting notes and the call's sentiment
          from the transcript.
        </Banner>
      )}
      {status === "PROCESSED" && !call.has_transcription && (
        <Banner tone="warning" icon="mic" title="No speech detected">
          {meeting.transcriptPlaceholder ||
            "Sidekick couldn't make out any words, so there's no transcript or summary."}
        </Banner>
      )}
    </>
  );

  const details = [
    { label: "Direction", value: call.direction },
    { label: "Number", value: call.tel_number || "Withheld" },
    { label: "Person", value: call.user_name },
    { label: "Extension", value: call.user_ext },
    { label: "Started", value: call.start },
    { label: "Ended", value: call.end },
    { label: "Length", value: length },
    { label: "Time zone", value: call.effective_timezone },
  ];

  return shell(
    <>
      {header}
      {states}
      <div
        style={{
          display: "flex",
          gap: "20px",
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            flex: "999 1 460px",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: "14px",
          }}
        >
          {call.has_transcription && (
            <>
              <Tabs
                ariaLabel="Call"
                value={tab}
                items={DETAIL_TABS}
                onChange={setTab}
              />
              {tab === "summary" && (
                <SummaryTab
                  meeting={meeting}
                  hasSummary={hasSummary}
                  subject="call"
                  emptyText={
                    canSummarise
                      ? "This call hasn't been summarised yet."
                      : undefined
                  }
                />
              )}
              {tab === "transcript" && (
                <TranscriptTab
                  meeting={meeting}
                  rename={renameSpeaker}
                  subject="call"
                  showTimes={false}
                  emptyText={
                    meeting.transcriptPlaceholder ||
                    "The transcript isn't ready yet."
                  }
                />
              )}
              {tab === "ask" && (
                <AskTab
                  ask={ask}
                  prompts={PROMPTS}
                  subject="call"
                  renderAnswer={(html) => <Answer html={html} />}
                  copyText={answerText}
                />
              )}
            </>
          )}
        </div>
        <DetailCard
          title="Call details"
          items={details}
          style={{ flex: "1 1 280px", minWidth: 0 }}
        />
      </div>

      {picking && (
        <SummariseTemplatePicker
          open
          onClose={() => setPicking(false)}
          onConfirm={runSummarise}
        />
      )}

      {emailing && (
        <CallEmailPanel email={email} onClose={() => setEmailing(false)} />
      )}
    </>,
  );
}
